import React from "react";
import { useLocation } from "react-router-dom";

function SkeletonBlock({ className = "" }) {
  return <div aria-hidden="true" className={`lightly-skeleton ${className}`} />;
}

function SkeletonLine({ width = "w-full", className = "" }) {
  return <SkeletonBlock className={`h-3 rounded-full ${width} ${className}`} />;
}

function Shell({ children, variant = "default", label = "Loading page" }) {
  return (
    <main
      className={`lightly-route-fallback lightly-route-fallback--${variant}`}
      aria-busy="true"
      aria-label={label}
    >
      <span className="sr-only">{label}</span>
      {children}
    </main>
  );
}

export function DonationMethodSkeletonGrid({ compact = false }) {
  return (
    <div className="public-giving-methods grid gap-3 sm:grid-cols-2">
      {[0, 1, 2, 3].map((item) => (
        <div
          key={item}
          className={`flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 ${
            compact ? "min-h-[70px] py-2.5" : "min-h-[86px] py-3"
          } shadow-[0_10px_26px_rgba(15,23,42,0.04)]`}
        >
          <SkeletonBlock className="h-10 w-10 shrink-0 rounded-2xl" />
          <div className="min-w-0 flex-1">
            <SkeletonLine width="w-24" />
            <SkeletonLine width="mt-2 w-32" />
          </div>
          <SkeletonBlock className="h-5 w-5 shrink-0 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export function DonationRouteSkeleton() {
  return (
    <Shell variant="donation" label="Loading giving page">
      <div className="mx-auto grid min-h-screen w-full max-w-6xl gap-6 px-4 py-6 md:grid-cols-[minmax(0,0.9fr)_minmax(360px,1fr)] md:px-6 md:py-8">
        <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm md:p-7">
          <SkeletonLine width="w-32" />
          <SkeletonBlock className="mt-5 aspect-[4/3] rounded-[24px]" />
          <div className="mt-6 grid gap-3">
            <SkeletonLine width="w-5/6" className="h-5" />
            <SkeletonLine width="w-2/3" />
            <SkeletonLine width="w-4/5" />
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {[0, 1, 2].map((item) => (
              <div key={item} className="rounded-2xl border border-slate-200 p-4">
                <SkeletonLine width="w-16" />
                <SkeletonLine width="mt-3 w-24" className="h-5" />
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm md:p-6">
          <SkeletonLine width="w-24" />
          <SkeletonLine width="mt-3 w-48" className="h-5" />
          <div className="mt-6 grid grid-cols-3 gap-2">
            {[0, 1, 2, 3, 4, 5].map((item) => (
              <SkeletonBlock key={item} className="h-12 rounded-full" />
            ))}
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <SkeletonBlock className="h-12 rounded-2xl" />
            <SkeletonBlock className="h-12 rounded-2xl" />
          </div>
          <div className="mt-7">
            <DonationMethodSkeletonGrid />
          </div>
          <div className="mt-6 flex gap-2">
            <SkeletonBlock className="h-11 w-24 rounded-full" />
            <SkeletonBlock className="h-11 flex-1 rounded-full" />
          </div>
        </section>
      </div>
    </Shell>
  );
}

export function AdminRouteSkeleton() {
  return (
    <Shell variant="admin" label="Loading admin workspace">
      <div className="flex min-h-screen bg-slate-50">
        <aside className="hidden w-[clamp(260px,24vw,360px)] shrink-0 border-r border-slate-200 bg-white p-5 lg:block">
          <SkeletonBlock className="h-14 rounded-2xl" />
          <div className="mt-8 grid gap-3">
            {[0, 1, 2, 3, 4, 5].map((item) => (
              <SkeletonBlock key={item} className="h-11 rounded-xl" />
            ))}
          </div>
        </aside>
        <section className="min-w-0 flex-1 px-4 py-5 sm:px-6 lg:px-10">
          <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm lg:hidden">
            <SkeletonBlock className="h-10 rounded-xl" />
          </div>
          <div className="max-w-7xl">
            <SkeletonLine width="w-28" />
            <SkeletonLine width="mt-3 w-72" className="h-7" />
            <div className="mt-6 grid gap-4 md:grid-cols-4">
              {[0, 1, 2, 3].map((item) => (
                <div key={item} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <SkeletonBlock className="h-10 w-10 rounded-xl" />
                  <SkeletonLine width="mt-5 w-20" />
                  <SkeletonLine width="mt-3 w-28" className="h-6" />
                </div>
              ))}
            </div>
            <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <SkeletonLine width="w-44" className="h-5" />
                <div className="mt-5 grid gap-3">
                  {[0, 1, 2, 3, 4].map((item) => (
                    <SkeletonBlock key={item} className="h-14 rounded-xl" />
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <SkeletonLine width="w-36" className="h-5" />
                <SkeletonBlock className="mt-5 h-64 rounded-xl" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </Shell>
  );
}

export function AttendanceRouteSkeleton() {
  return (
    <Shell variant="attendance" label="Loading attendance workspace">
      <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-10">
        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <SkeletonLine width="w-32" />
              <SkeletonLine width="mt-3 w-64" className="h-7" />
            </div>
            <SkeletonBlock className="h-11 w-36 rounded-full" />
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((item) => (
              <SkeletonBlock key={item} className="h-28 rounded-2xl" />
            ))}
          </div>
          <div className="mt-6 grid gap-4 lg:grid-cols-[0.8fr_1.2fr]">
            <SkeletonBlock className="h-72 rounded-2xl" />
            <div className="rounded-2xl border border-slate-200 p-4">
              {[0, 1, 2, 3, 4, 5].map((item) => (
                <SkeletonBlock key={item} className="mb-3 h-12 rounded-xl last:mb-0" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}

export function BuilderRouteSkeleton() {
  return (
    <Shell variant="builder" label="Loading design workspace">
      <div className="grid min-h-screen gap-0 bg-slate-50 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="hidden border-r border-slate-200 bg-white p-5 lg:block">
          <SkeletonBlock className="h-12 rounded-2xl" />
          <div className="mt-6 grid gap-3">
            {[0, 1, 2, 3, 4, 5].map((item) => (
              <SkeletonBlock key={item} className="h-14 rounded-2xl" />
            ))}
          </div>
        </aside>
        <section className="px-4 py-5 sm:px-6 lg:px-10">
          <SkeletonLine width="w-40" />
          <SkeletonLine width="mt-3 w-72" className="h-7" />
          <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
            <SkeletonBlock className="min-h-[520px] rounded-[28px]" />
            <div className="grid content-start gap-4">
              <SkeletonBlock className="h-40 rounded-2xl" />
              <SkeletonBlock className="h-56 rounded-2xl" />
            </div>
          </div>
        </section>
      </div>
    </Shell>
  );
}

export function PublicRouteSkeleton() {
  return (
    <Shell variant="public" label="Loading public page">
      <div className="min-h-screen bg-white">
        <header className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <SkeletonBlock className="h-11 w-11 rounded-2xl" />
            <SkeletonLine width="w-36" className="h-4" />
          </div>
          <div className="hidden gap-3 md:flex">
            {[0, 1, 2, 3].map((item) => (
              <SkeletonBlock key={item} className="h-9 w-20 rounded-full" />
            ))}
          </div>
        </header>
        <section className="mx-auto grid max-w-7xl gap-7 px-4 pb-8 pt-3 sm:px-6 md:grid-cols-[1fr_0.8fr] lg:px-10">
          <div className="py-8">
            <SkeletonLine width="w-32" />
            <SkeletonLine width="mt-5 w-full" className="h-10" />
            <SkeletonLine width="mt-3 w-5/6" className="h-10" />
            <SkeletonLine width="mt-5 w-4/5" />
            <SkeletonLine width="mt-2 w-2/3" />
            <div className="mt-7 flex gap-3">
              <SkeletonBlock className="h-12 w-36 rounded-full" />
              <SkeletonBlock className="h-12 w-32 rounded-full" />
            </div>
          </div>
          <SkeletonBlock className="aspect-[4/3] min-h-[260px] rounded-[28px]" />
        </section>
        <section className="border-t border-slate-100 bg-slate-50 px-4 py-8 sm:px-6 lg:px-10">
          <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-3">
            {[0, 1, 2].map((item) => (
              <SkeletonBlock key={item} className="h-48 rounded-2xl" />
            ))}
          </div>
        </section>
      </div>
    </Shell>
  );
}

export function MemberRouteSkeleton() {
  return (
    <Shell variant="member" label="Loading member portal">
      <div className="flex min-h-screen bg-slate-50">
        <aside className="hidden w-72 shrink-0 border-r border-slate-200 bg-white p-5 lg:block">
          <SkeletonBlock className="h-16 rounded-2xl" />
          <div className="mt-8 grid gap-3">
            {[0, 1, 2, 3].map((item) => (
              <SkeletonBlock key={item} className="h-11 rounded-xl" />
            ))}
          </div>
        </aside>
        <section className="min-w-0 flex-1 px-4 py-5 sm:px-8 lg:px-14">
          <SkeletonBlock className="mb-5 h-14 rounded-2xl lg:hidden" />
          <div className="grid gap-4 lg:grid-cols-[0.8fr_1.2fr]">
            <SkeletonBlock className="h-80 rounded-[28px]" />
            <div className="grid gap-4">
              <SkeletonBlock className="h-36 rounded-2xl" />
              <SkeletonBlock className="h-52 rounded-2xl" />
            </div>
          </div>
        </section>
      </div>
    </Shell>
  );
}

function routeMode(pathname = "") {
  const path = pathname.toLowerCase();
  if (/(^|\/)(donate|org-donate)(\/|$)/.test(path) || /\/campaigns\/[^/]+\/(?:donate|.*\/donate)$/.test(path)) {
    return "donation";
  }
  if (/\/(?:attendance|checkin|children-checkin|children-checkout|kiosk)(\/|$)/.test(path)) {
    return "attendance";
  }
  if (/\/(?:design|media-vault|visitors)(\/|$)/.test(path)) {
    return "builder";
  }
  if (/^\/(?:admin|platform\/o\/[^/]+)(\/|$)/.test(path) || /\/(?:donors|reports|funding|notifications|automations|teams|roles)(\/|$)/.test(path)) {
    return "admin";
  }
  if (/\/(?:me|my-giving|app\/teams)(\/|$)/.test(path)) {
    return "member";
  }
  return "public";
}

export default function RouteLoadingFallback() {
  const location = useLocation();
  const mode = routeMode(location.pathname);

  if (mode === "donation") return <DonationRouteSkeleton />;
  if (mode === "attendance") return <AttendanceRouteSkeleton />;
  if (mode === "builder") return <BuilderRouteSkeleton />;
  if (mode === "admin") return <AdminRouteSkeleton />;
  if (mode === "member") return <MemberRouteSkeleton />;
  return <PublicRouteSkeleton />;
}
